/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { generateCacheKey, parameterize } from '@msdyn365-commerce-modules/retail-actions';
import { getSubCategoryStructureControllerAsync } from './DataActionExtension.g';
import { ISubCategoryStructureEntity } from './DataServiceEntities.g';

/**
 * GetSubcategories Input Action
 */
export class GetSubcategoriesInput implements Msdyn365.IActionInput {
    // TODO: Construct the input needed to run the action
    public categoryId?: number;
    public categorySlug?: string;
    private readonly apiSettings: Msdyn365.ICommerceApiSettings;

    constructor(requestContext: Msdyn365.IRequestContext) {
        let categoryId;
        let categorySlug;
        if (requestContext.query && requestContext.query.categoryId) {
            categoryId = Number(requestContext.query.categoryId);

            if (Number.isNaN(categoryId)) {
                throw new Error('Failed to cast category id into a number.');
            }
        } else if (requestContext.urlTokens && requestContext.urlTokens.recordId) {
            categoryId = Number(requestContext.urlTokens.recordId);
            if (Number.isNaN(categoryId)) {
                throw new Error('Failed to cast category id into a number.');
            }
        } else if (requestContext.urlTokens && requestContext.urlTokens.categories && requestContext.urlTokens.categories.length > 0) {
            const categories: string[] = <string[]>(<unknown>requestContext.urlTokens.categories);

            if (categories.length === 0) {
                throw new Error('Failed to get category name from urlTokens');
            }

            // TODO: fix getParameterizeFeatureFlags to take requestContext as input.
            const tempActionContext = {
                requestContext: requestContext
            };
            categorySlug = `/${categories
                .map(category => {
                    // @ts-expect-error TODO: fix getParameterizeFeatureFlags to take requestContext as an input
                    const parameterizeFeatureFlags: IParameterizeFeatureFlags = getParameterizeFeatureFlags(tempActionContext);
                    return parameterize(category, undefined, parameterizeFeatureFlags);
                })
                .join('/')}`;
        }
        this.categoryId = categoryId;
        this.categorySlug = categorySlug;
        this.apiSettings = requestContext.apiSettings;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'base', value: 'GetSubCategories' },
            { key: 'categoryId', value: this.categoryId },
            { key: 'categorySlug', value: this.categorySlug }
        );
    public getCacheObjectType = () => 'GetSubCategoryStructureEntity';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetSubcategoriesData {
    text: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createGetSubcategoriesInput = (args: Msdyn365.ICreateActionContext<Msdyn365.IGeneric<Msdyn365.IAny>>): Msdyn365.IActionInput => {
    if (args && args.requestContext) {
        return new GetSubcategoriesInput(args.requestContext);
    }
    throw new Error('Please specify categoryId query string in request.');
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function GetSubCategoriesAction(input: GetSubcategoriesInput, ctx: Msdyn365.IActionContext): Promise<ISubCategoryStructureEntity[]> {
    // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;
    const getSubcategoryInput = new GetSubcategoriesInput(ctx.requestContext);
    const subcategories = await getSubCategoryStructureControllerAsync(
        { callerContext: ctx, bypassCache: 'none' },
        getSubcategoryInput.categoryId ?? 0
    );

    return subcategories;

    // TODO: Uncomment the below line to get the value from a service
    // const response = await Msdyn365.sendRequest<IGetSubcategoriesData[]>('/get/example/id/1', 'get');
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ISubCategoryStructureEntity[]>>GetSubCategoriesAction,
    // TODO: Give your data action a unique id
    id: 'GetSubcategories',
    input: createGetSubcategoriesInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
